import { useCallback, useMemo } from 'react';
import { differenceInWeeks } from 'date-fns/differenceInWeeks';
import { differenceInHours } from 'date-fns/differenceInHours';
import { useOnboardingState } from 'state/useOnboardingState';
import { getLastPushNotificationTimestamp } from 'state/localStorage';
import { InboxMessage } from 'features/inboxMessages/inboxMessages.types';
import { ENV } from 'shared/ENV';
import { useInboxMessages } from '../useInboxMessages';

type UsePopupMessageHookReturn = {
  message: InboxMessage | undefined;
  setMessageAsRead: () => void;
};

const FIVE_MINUTES_MS = 1000 * 60 * 5; // 5 minutes

const MIN_APP_USAGE_HOURS = 24;

const shouldShowPopupMessage = (input: {
  showTutorialSteps: boolean;
  pushNotifLast5Mins: boolean;
  appUsageInHours: number;
}): boolean => {
  if (ENV.VITE_SKIP_INBOX_MESSAGE_CHECKS) return true;

  const { showTutorialSteps, pushNotifLast5Mins, appUsageInHours } = input;

  return (
    !showTutorialSteps &&
    !pushNotifLast5Mins &&
    appUsageInHours > MIN_APP_USAGE_HOURS
  );
};

export const usePopupMessage = (): UsePopupMessageHookReturn => {
  const { popupMessages, setMessagesAsRead } = useInboxMessages();
  const { showTutorialSteps, nativeOnboardedAt } = useOnboardingState();

  const message = useMemo(() => {
    if (!popupMessages.length) return undefined;

    const today = new Date();

    const lastPushNotificationTimestamp = getLastPushNotificationTimestamp();

    const pushNotifLast5Mins = lastPushNotificationTimestamp
      ? today.getTime() - lastPushNotificationTimestamp <= FIVE_MINUTES_MS
      : false;

    const appUsageInHours = nativeOnboardedAt
      ? Math.abs(
          differenceInHours(today, new Date(nativeOnboardedAt), {
            roundingMethod: 'round',
          }),
        )
      : 0;

    if (
      !shouldShowPopupMessage({
        showTutorialSteps,
        pushNotifLast5Mins,
        appUsageInHours,
      })
    ) {
      return undefined;
    }

    const sortedDesc = popupMessages.sort((a, b) => {
      return (
        new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
      );
    });

    const filteredMessages = sortedDesc.filter((msg) => {
      const withinLast2Weeks =
        Math.abs(
          differenceInWeeks(today, new Date(msg.dateCreated), {
            roundingMethod: 'round',
          }),
        ) <= 2;

      return withinLast2Weeks;
    });

    return filteredMessages[0];
  }, [nativeOnboardedAt, popupMessages, showTutorialSteps]);

  const setMessageAsRead = useCallback(() => {
    setMessagesAsRead(popupMessages.map((msg) => msg.id));
  }, [setMessagesAsRead, popupMessages]);

  return { message, setMessageAsRead };
};
